import React, { useCallback, useLayoutEffect, useMemo } from 'react'
import { usePageStateContext } from '../hooks'
import { ComponentPageStateContext } from './component-page-state-context'
import { ComponentState } from './page-state-context'

export interface ComponentPageStateProviderProps {
  componentId: string
  componentType: string
  initialMetadata?: ComponentState['metadata']
  children: React.ReactNode
}

export const ComponentPageStateProvider: React.FC<
  ComponentPageStateProviderProps
> = ({ componentId, componentType, initialMetadata, children }) => {
  const {
    registerComponent,
    unregisterComponent,
    mergeComponentState: mergeComponentStateFn,
    setComponentState: setComponentStateFn,
  } = usePageStateContext()

  const mergeComponentState = useCallback(
    (state: Partial<ComponentState['state']>) => {
      mergeComponentStateFn(componentId, state)
    },
    [componentId, mergeComponentStateFn],
  )

  const setComponentState = useCallback(
    (state: ComponentState['state']) => {
      setComponentStateFn(componentId, state)
    },
    [componentId, setComponentStateFn],
  )

  const contextValue = useMemo(
    () => ({
      componentId,
      componentType,
      mergeComponentState,
      setComponentState,
    }),
    [componentId, componentType, mergeComponentState, setComponentState],
  )

  useLayoutEffect(() => {
    // Register component on mount
    registerComponent({
      componentId,
      componentType,
      state: {},
      metadata: initialMetadata,
    })

    // Unregister on unmount
    return () => unregisterComponent(componentId)
  }, [
    componentId,
    componentType,
    initialMetadata,
    registerComponent,
    unregisterComponent,
  ])

  return (
    <ComponentPageStateContext.Provider value={contextValue}>
      {children}
    </ComponentPageStateContext.Provider>
  )
}
